/**
 * AgentWorkflow — Visual pipeline timeline with connected stage nodes.
 * Auto-loads the most recent document if none specified.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { useAuth } from '../../../components/AuthProvider';
import { listDocuments } from '../../../api/documents';
import apiClient from '../../../api/client';
import type { WorkflowResponse, WorkflowStage } from '../../../api/types';

const stageLabels: Record<string, string> = {
  uploaded: 'Uploaded',
  ingestion: 'Ingestion',
  privacy_scan: 'Privacy Scan',
  cti_extraction: 'CTI Extraction',
  mitre_mapping: 'MITRE Mapping',
  risk_scoring: 'Risk Scoring',
  knowledge_indexing: 'Knowledge Indexing',
  report_generation: 'Report Generation',
  audit: 'Audit',
  completed: 'Completed',
};

function StageIndicator({ stage }: { stage: WorkflowStage }) {
  const statusStyles: Record<string, string> = {
    completed: 'bg-green-500 border-green-600',
    in_progress: 'bg-blue-500 border-blue-600 animate-pulse',
    pending: 'bg-gray-300 border-gray-400',
    failed: 'bg-red-500 border-red-600',
  };

  const textStyles: Record<string, string> = {
    completed: 'text-green-700 font-semibold',
    in_progress: 'text-blue-700 font-semibold',
    pending: 'text-gray-500',
    failed: 'text-red-700 font-semibold',
  };

  return (
    <div className="relative flex items-start gap-4 pl-2">
      {/* Dot */}
      <div className={`w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 mt-1 ${statusStyles[stage.status] || statusStyles.pending}`} />
      {/* Content */}
      <div className="flex-1 pb-6">
        <p className={`text-body ${textStyles[stage.status] || textStyles.pending}`}>
          {stageLabels[stage.name] || stage.name}
        </p>
        <p className="text-[13px] text-slate-muted capitalize">{stage.status.replace('_', ' ')}</p>
        {stage.completed_at && (
          <p className="text-[12px] text-slate-muted mt-1">
            {new Date(stage.completed_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}

export function AgentWorkflow() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const documentId = searchParams.get('doc') || '';
  const [workflow, setWorkflow] = useState<WorkflowResponse | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<string>(documentId);
  const [documents, setDocuments] = useState<{document_id: string; file_name: string; status: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load documents list
  useEffect(() => {
    if (!user) return;
    listDocuments(user.tenantId).then(resp => {
      setDocuments(resp.documents);
      if (!selectedDoc && resp.documents.length > 0) {
        const latest = resp.documents[resp.documents.length - 1];
        setSelectedDoc(latest.document_id);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch workflow for selected document
  useEffect(() => {
    if (!selectedDoc || !user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    apiClient.get(`/cti/workflow/${selectedDoc}`, {
      params: { tenant_id: user.tenantId },
    }).then(resp => {
      setWorkflow(resp.data);
    }).catch(() => {
      setError('Failed to load workflow status.');
    }).finally(() => {
      setLoading(false);
    });
  }, [selectedDoc, user]);

  if (loading) return <LoadingState message="Loading pipeline status..." />;
  if (error) return <ErrorState message={error} onRetry={() => setSelectedDoc(selectedDoc)} />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">Agent Workflow</h1>
        <p className="text-body text-slate-muted mt-1">
          Pipeline processing status for documents
        </p>
      </div>

      {/* Document selector */}
      {documents.length > 0 && (
        <div className="glass-card">
          <label className="text-[16px] font-semibold text-slate-muted block mb-2">Select Document</label>
          <select
            value={selectedDoc}
            onChange={(e) => setSelectedDoc(e.target.value)}
            className="w-full p-3 rounded-card border-2 border-gray-200 text-body text-slate-text bg-white focus:border-primary-teal focus:outline-none"
          >
            <option value="">-- Select a document --</option>
            {documents.map(doc => (
              <option key={doc.document_id} value={doc.document_id}>
                {doc.file_name} ({doc.status})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Workflow display */}
      {workflow && (
        <>
          {/* Current stage badge */}
          <div className="glass-card flex items-center justify-between">
            <div>
              <p className="text-[16px] font-semibold text-slate-muted">Current Stage</p>
              <p className="text-section font-bold text-primary-blue">
                {stageLabels[workflow.current_stage] || workflow.current_stage}
              </p>
            </div>
            <p className="text-[14px] font-mono bg-gray-100 px-3 py-1 rounded">
              {workflow.document_id.slice(0, 8)}...
            </p>
          </div>

          {/* Visual Pipeline Timeline */}
          <div className="glass-card">
            <h3 className="text-section-sm font-bold text-slate-text mb-6">Pipeline Stages</h3>
            <div className="relative">
              {/* Vertical connecting line */}
              <div className="absolute left-[10px] top-3 bottom-3 w-0.5 bg-gray-300" />
              {workflow.stages.map((stage) => (
                <StageIndicator key={stage.name} stage={stage} />
              ))}
            </div>
          </div>
        </>
      )}

      {!workflow && !loading && documents.length === 0 && (
        <div className="glass-card text-center py-12">
          <p className="text-body text-slate-muted">
            No documents uploaded yet. Upload a CTI file to see the pipeline workflow.
          </p>
        </div>
      )}
    </div>
  );
}
